import { PROMOTION_DETAILS_KEY } from 'presentation/events/services/promotionsService';
import {
	getCreativeNameFromPathname,
	getPromotionNameFromGtmClassElement,
} from 'presentation/events/utils/promotionsUtils';
import { ClickSource, SelectPromotionEventProps } from './types';

export const getClickSourceFromAnchorElement = (anchorElement: Element): ClickSource =>
	anchorElement.querySelector('img') ? 'image' : 'text';

export const getCreativeSlotFromDataDiv = (
	dataDivElement: Element,
	promotionName: string
): number => {
	let creativeSlot = 1;
	let foundGtmClassElement = false;
	const seenPromotionNames = new Set<string>();

	const gtmClassElements = dataDivElement.querySelectorAll('.gtm_class');

	gtmClassElements.forEach((otherGtmClassElement) => {
		const otherPromotionName = getPromotionNameFromGtmClassElement(otherGtmClassElement);

		if (!otherPromotionName) {
			return;
		}

		if (seenPromotionNames.has(otherPromotionName)) {
			return;
		}

		seenPromotionNames.add(otherPromotionName);

		if (!foundGtmClassElement && otherPromotionName !== promotionName) {
			creativeSlot += 1;
		}

		if (promotionName === otherPromotionName) {
			foundGtmClassElement = true;
		}
	});

	return creativeSlot;
};

export const extractSelectPromotionEventProps = (
	target: EventTarget | null,
	pathname: string
): SelectPromotionEventProps | null => {
	if (!(target instanceof HTMLElement)) {
		return null;
	}

	const dataDivElement = target.closest('[data-div]');

	if (!dataDivElement) {
		return null;
	}

	const promotionId = dataDivElement.getAttribute('data-div');

	if (!promotionId) {
		return null;
	}

	const gtmClassElement = target.closest('.gtm_class');

	if (!gtmClassElement) {
		return null;
	}

	const promotionName = getPromotionNameFromGtmClassElement(gtmClassElement);

	if (!promotionName) {
		return null;
	}

	const anchorElement = target.closest('a');

	if (!anchorElement) {
		return null;
	}

	const href = anchorElement.getAttribute('href');

	const hrefPathname = href ? href.split('?')[0].split('#')[0] : '';
	const keyWords = hrefPathname.split('/').filter((word) => word !== '');

	const creativeName = getCreativeNameFromPathname(pathname);

	const creativeSlot = getCreativeSlotFromDataDiv(dataDivElement, promotionName);

	const clickSource = getClickSourceFromAnchorElement(anchorElement);

	return {
		promotionId,
		promotionName,
		creativeName,
		creativeSlot: creativeSlot.toString(),
		keyWords,
		clickSource,
	};
};

export const updateSelectedPromotionKeyWords = (keyWords: string[]) => {
	try {
		if (typeof window === 'undefined' || !window.sessionStorage) return;

		const raw = sessionStorage.getItem(PROMOTION_DETAILS_KEY);
		let parsed: Record<string, unknown> = {};

		if (raw) {
			const temp = JSON.parse(raw);
			if (temp && typeof temp === 'object') {
				parsed = temp;
			}
		}

		parsed.key_words = keyWords;
		sessionStorage.setItem(PROMOTION_DETAILS_KEY, JSON.stringify(parsed));
	} catch (error) {
		console.error('Error updating promDetails key_words:', error);
	}
};

export const updateSelectedPromotionListPartnumbers = (
	partnumbers: string[],
	currentPathname: string
): boolean => {
	try {
		const raw = sessionStorage.getItem(PROMOTION_DETAILS_KEY);
		if (!raw) return false;

		const parsed = JSON.parse(raw);
		if (!parsed || typeof parsed !== 'object') return false;
		if (!Array.isArray(parsed.key_words) || parsed.key_words.length === 0) return false;

		const matchesDestination = parsed.key_words.every((word: unknown) => {
			if (typeof word !== 'string') return false;
			const cleanWord = word.split('?')[0].split('#')[0];
			return cleanWord !== '' && currentPathname.includes(cleanWord);
		});
		if (!matchesDestination) return false;

		parsed.list_partnumbers = partnumbers;
		sessionStorage.setItem(PROMOTION_DETAILS_KEY, JSON.stringify(parsed));
		return true;
	} catch (error) {
		console.error('Error updating promDetails list_partnumbers:', error);
		return false;
	}
};
